import { useCallback, useEffect } from 'react'
import { useConversationSync } from '../hooks/useConversationSync'
import { useConversation } from '../hooks/useConversations'
import { useCrmConnections } from '../hooks/useCrmConnections'
import { useCrmMapping, useActiveCrm } from '../hooks/useCrmMapping'
import { openCrmSyncReview } from '../lib/crm'

interface Props {
  conversationUrn: string | null
  participantProfileUrn: string | null
  isGroup: boolean
  onToast?: (message: string, tone: 'success' | 'error' | 'info') => void
}

export default function ComposeSyncButton({
  conversationUrn,
  participantProfileUrn,
  isGroup,
  onToast,
}: Props) {
  const { connections } = useCrmConnections()
  const activeCrm = useActiveCrm(connections)
  const { mapping } = useCrmMapping(participantProfileUrn, activeCrm)
  const conversation = useConversation(conversationUrn)
  const { syncing, progress, unsyncedCount, run } = useConversationSync(
    conversationUrn,
    activeCrm,
  )

  const runConfirmedSync = useCallback(async () => {
    const report = await run()
    if (!report) return
    if (report.skippedReason) {
      onToast?.(`Sync skipped: ${report.skippedReason}`, 'info')
      return
    }
    if (report.burstsFailed > 0) {
      onToast?.(
        `Synced ${report.burstsSucceeded} of ${report.bursts}, ${report.burstsFailed} failed`,
        'error',
      )
    } else if (report.toSync === 0) {
      onToast?.('All messages already synced', 'info')
    } else {
      const msgWord = report.toSync === 1 ? 'message' : 'messages'
      const intWord = report.bursts === 1 ? 'interaction' : 'interactions'
      onToast?.(
        `Synced ${report.toSync} ${msgWord} as ${report.bursts} ${intWord}. Future messages will sync automatically.`,
        'success',
      )
    }
  }, [onToast, run])

  useEffect(() => {
    if (!conversationUrn || !activeCrm) return
    const handler = (event: MessageEvent) => {
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'CRM_SYNC_REQUEST') return
      if (event.data.conversationUrn !== conversationUrn) return
      if (event.data.crmName !== activeCrm) return
      void runConfirmedSync()
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [activeCrm, conversationUrn, runConfirmedSync])

  // Visibility rules
  if (isGroup) return null
  if (!activeCrm) return null
  if (connections.affinity?.status !== 'active') return null
  if (!connections.affinity?.enrichmentEnabled) return null
  if (!conversationUrn || !participantProfileUrn) return null

  const hasResolvedTarget = mapping?.status === 'matched' || mapping?.status === 'created'

  const handleClick = async () => {
    try {
      await openCrmSyncReview({
        conversationUrn,
        profileUrn: participantProfileUrn,
        displayName: conversation?.participantName ?? mapping?.crmContactName ?? null,
        headline: conversation?.participantHeadline ?? null,
        profileImage: conversation?.participantProfileImage ?? null,
      })
      onToast?.(
        hasResolvedTarget
          ? 'Review the Affinity target in Slash before syncing'
          : 'Choose or create the Affinity person in Slash',
        'info',
      )
    } catch (e) {
      onToast?.(e instanceof Error ? e.message : 'Could not open Slash review panel', 'error')
    }
  }

  const disabled = syncing

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={
        syncing
          ? `Syncing ${progress?.done ?? 0} of ${progress?.total ?? 0} to Affinity...`
          : hasResolvedTarget
            ? 'Review Affinity target before syncing'
            : 'Choose an Affinity person before syncing'
      }
      className="relative flex h-[30px] w-[30px] items-center justify-center rounded-md hover:bg-zinc-100 disabled:opacity-50"
    >
      {/* Upload-cloud glyph matches the other ComposeBar icon sizing */}
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      {/* Unsynced badge — small number in the corner, only when there's something to sync */}
      {!syncing && hasResolvedTarget && unsyncedCount > 0 && (
        <span className="absolute -right-0.5 -top-0.5 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-zinc-900 px-1 text-[9px] font-semibold text-white">
          {unsyncedCount > 99 ? '99+' : unsyncedCount}
        </span>
      )}

      {!syncing && !hasResolvedTarget && (
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-white bg-amber-500" />
      )}

      {/* Spinner dot while syncing */}
      {syncing && (
        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse rounded-full bg-amber-500" />
      )}
    </button>
  )
}
